import React, { useState } from 'react';
import {
  Box, Typography, Chip, Button, IconButton,
  Tab, Tabs, Paper, Fab, LinearProgress, Skeleton,
  alpha
} from '@mui/material';
import {
  Users, RefreshCw, Plus, Tag, Shield, Wifi,
  Check, AlertTriangle, Clock, Smartphone, Crown,
  ArrowRight, Phone
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import ShareCircleButton from '../ShareCircleButton/ShareCircleButton';
import { useCircleData } from './CircleDataContext';
import { useAuth0 } from '@auth0/auth0-react';

// Import service modules
import { circleService } from '../../services/circleService';

/**
 * CircleTabListView Component
 * Displays the list of circles with filtering tabs
 * Uses CircleDataContext for accurate pricing data
 */
const CircleTabListView = ({
  circles,
  ownedCircles,
  joinedCircles,
  dataInitialized,
  onCircleSelect,
  onRefresh,
  refreshAnimation,
  getCarrierColor,
  getStatusColor,
  darkMode = false,
  onPortAction
}) => {
  // Tab state for filtering
  const [activeTab, setActiveTab] = useState(0); // 0 = All, 1 = Owned, 2 = Joined

  // Access CircleDataContext for refreshing data and pricing info
  const { refreshData, circlePriceMap } = useCircleData();

  // Auth0 hook for authentication
  const { getAccessTokenSilently } = useAuth0();

  // Handle tab change
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Filter circles based on active tab
  const getVisibleCircles = () => {
    switch (activeTab) {
      case 0: // All
        return circles;
      case 1: // Owned
        return ownedCircles;
      case 2: // Joined
        return joinedCircles;
      default:
        return circles;
    }
  };

  // Handle refresh button click - refresh both local and context data
  const handleRefresh = () => {
    onRefresh(); // This will handle setting refreshAnimation=true internally
    refreshData(); // Refresh CircleDataContext data
  };

  // Navigate to create circle page
  const handleCreateCircle = async () => {
    try {
      // Navigate to the create circle page
      window.location.href = '/create-circle';
    } catch (error) {
      console.error("Error navigating to create circle page:", error);
    }
  };

  // Format currency helper
  const formatCurrency = (amount) => {
    if (amount === undefined || amount === null) return '0.00';
    return typeof amount === 'number'
      ? amount.toFixed(2)
      : parseFloat(amount || 0).toFixed(2);
  };

  // Share circle functionality
  const handleShareCircle = async (e, circle) => {
    e.stopPropagation(); // Prevent card click event

    try {
      // Get auth token
      const token = await getAccessTokenSilently({
        audience: process.env.REACT_APP_AUTH0_AUDIENCE,
        scope: 'openid profile email'
      });

      // In a real implementation, call the service
      // Example: await circleService.shareCircle(circle.id, {}, token);

      console.log("Sharing circle:", circle.name);
    } catch (error) {
      console.error("Error sharing circle:", error);
    }
  };

  return (
    <Box sx={{
      width: '100%',
      position: 'relative',
      pb: { xs: 8, sm: 10 }, // Bottom padding for FAB
    }}>
      {/* Title Header */}
      <Box sx={{
        display: 'flex',
        alignItems: 'center',
        mb: 2,
        background: 'transparent !important'
      }}>
        <Box sx={{ flex: 1 }}>
          <Typography
            variant="body2"
            color={darkMode ? "rgba(255,255,255,0.7)" : "text.secondary"}
            sx={{ fontWeight: 500 }}
          >
            Join or manage your subscription circles to save on mobile plans
          </Typography>
        </Box>

        {/* Refresh button with animation */}
        <motion.div
          animate={{ rotate: refreshAnimation ? 360 : 0 }}
          transition={{ duration: 0.8, type: 'spring' }}
        >
          <IconButton
            onClick={handleRefresh}
            className="refresh-button"
            sx={{
              p: 1.5,
              bgcolor: darkMode ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.03)',
              border: '1px solid',
              borderColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.04)',
              borderRadius: '12px',
              boxShadow: darkMode ? '0 2px 8px rgba(0,0,0,0.2)' : '0 2px 8px rgba(0,0,0,0.03)',
              color: darkMode ? 'rgba(255,255,255,0.8)' : undefined,
              '&:hover': {
                bgcolor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
                transform: 'translateY(-2px)',
                boxShadow: darkMode ? '0 4px 12px rgba(0,0,0,0.25)' : '0 4px 12px rgba(0,0,0,0.05)'
              },
              transition: 'all 0.2s ease'
            }}
          >
            <RefreshCw size={18} />
          </IconButton>
        </motion.div>
      </Box>

      {/* Tabs for filtering circles */}
      {dataInitialized && (
        <Paper
          elevation={0}
          sx={{
            borderRadius: '18px',
            mb: 3,
            overflow: 'hidden',
            // Apply a smooth background gradient for the entire container based on the design
            bgcolor: darkMode
              ? 'rgba(30, 30, 48, 0.4)'
              : '#FFFFFF',
            boxShadow: darkMode
              ? '0 4px 20px rgba(0, 0, 0, 0.15)'
              : '0 4px 20px rgba(0, 0, 0, 0.02)',
            position: 'relative'
          }}
        >
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            sx={{
              minHeight: '48px',
              position: 'relative',
              zIndex: 2,
              '& .MuiTabs-indicator': {
                display: 'none' // Hide default indicator
              },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontWeight: 500,
                fontSize: '0.9rem',
                minHeight: '44px',
                transition: 'all 0.2s ease',
                color: darkMode ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.6)',
                borderRadius: '12px',
                py: 1.2,
                zIndex: 2,
                '&.Mui-selected': {
                  color: darkMode ? '#FFF' : '#FF4775',
                  fontWeight: 600
                },
                '&:hover': {
                  color: darkMode ? 'rgba(255,255,255,0.9)' : '#FF5985',
                  bgcolor: 'transparent'
                }
              }
            }}
          >
            <Tab
              label={`All Circles (${circles.length})`}
            />
            <Tab
              label={`Circles I Own (${ownedCircles.length})`}
            />
            <Tab
              label={`Circles I've Joined (${joinedCircles.length})`}
            />
          </Tabs>

          {/* Active Tab Indicator */}
          <Box
            component={motion.div}
            initial={false}
            animate={{
              left: `${(activeTab * 100) / 3}%`,
              width: '33.33%'
            }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            sx={{
              position: 'absolute',
              height: '100%',
              top: 0,
              bgcolor: darkMode ? 'rgba(255,255,255,0.06)' : 'white',
              borderRadius: '12px',
              boxShadow: '0 2px 8px rgba(0,0,0,0.03)',
              zIndex: 1
            }}
          />
        </Paper>
      )}

      {/* Empty State */}
      {getVisibleCircles().length === 0 && dataInitialized && (
        <EmptyStateView
          activeTab={activeTab}
          darkMode={darkMode}
          onCreateCircle={handleCreateCircle}
        />
      )}

      {/* Circles Grid View */}
      {getVisibleCircles().length > 0 && (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            sm: 'repeat(2, 1fr)',
            md: 'repeat(2, 1fr)',
            lg: 'repeat(3, 1fr)'
          },
          gap: 3,
          width: '100%',
          pb: 2
        }}>
          <AnimatePresence>
            {getVisibleCircles().map((circle, index) => (
              <motion.div
                key={circle.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                className="circle-card-wrapper"
              >
                <CircleCard
                  circle={circle}
                  onSelect={() => onCircleSelect(circle)}
                  getCarrierColor={getCarrierColor}
                  getStatusColor={getStatusColor}
                  darkMode={darkMode}
                  formatCurrency={formatCurrency}
                  pricingInfo={circlePriceMap[circle.id]}
                  onShareCircle={(e) => handleShareCircle(e, circle)}
                  onPortAction={onPortAction}
                />
              </motion.div>
            ))}
          </AnimatePresence>
        </Box>
      )}

      {/* Add Circle Floating Action Button */}

    </Box>
  );
};

/**
 * CircleCard Component
 * Individual card displaying a circle's details with accurate pricing from CircleDataContext
 * UPDATED: Fixed port actions to handle ESIM Initiated status correctly and pass stripe_subscription_id
 */
const CircleCard = ({
  circle,
  onSelect,
  getCarrierColor,
  getStatusColor,
  darkMode = false,
  formatCurrency,
  pricingInfo,
  onShareCircle,
  onPortAction
}) => {
  // Get default pricing values for initial render or when context data is still loading
  const initialPrice = {
    currentPrice: 0,
    nextTierPrice: null,
    savingsPerLine: 0,
    isMaxTier: false,
    hasLoaded: false,
    membersToNextTier: 0
  };

  // Use context pricing info if available, otherwise use initial values
  const pricing = pricingInfo || initialPrice;

  // Determine if we're still waiting for pricing data
  const isPricingLoading = !pricingInfo?.hasLoaded;

  // Hover state for animations
  const [isHovered, setIsHovered] = useState(false);

  // More robust helper functions to extract circle properties with proper normalization

  // Get the subscription status from any available property and normalize it
  const getSubscriptionStatus = (circle) => {
    // If the circle is viewed as a subscription (not as an owner)
    // prioritize the statusDisplay since that's what's shown in the UI
    let status = '';

    if (circle.source === 'subscription' && circle.statusDisplay) {
      status = circle.statusDisplay;
    } else {
      // Try all possible property names for status
      status = circle.subscription_status ||
        circle.subscriptionStatus ||
        circle.statusDisplay ||
        circle.status ||
        '';
    }

    return status;
  };

  // Get the joining condition from any available property and normalize it
  const getJoiningCondition = (circle) => {
    // Check all possible property names 
    return circle.joining_condition ||
      circle.joiningCondition ||
      circle.joining_path ||
      circle.joiningPath ||
      '';
  };

  // Get the shipping method from any available property and normalize it
  const getShippingMethod = (circle) => {
    // Check all possible property names
    const method = circle.shipping_method ||
      circle.shippingMethod ||
      circle.shipping ||
      '';

    // Log the method we found with the source property for debugging
    console.log(`Shipping method for ${circle.name}:`, {
      determined: method,
      fromProperty: circle.shipping_method ? 'shipping_method' :
        (circle.shippingMethod ? 'shippingMethod' :
          (circle.shipping ? 'shipping' : 'none')),
      circle: circle
    });

    return method;
  };

  // Get the Stripe subscription ID from any available property
  // Complete getSubscriptionId function with correct formatting
  const getSubscriptionId = (circle) => {
    // For debugging - Log that we're checking for subscription ID in the circle object
    console.log(`Pre-API check: Looking for subscription ID in circle ${circle.name}:`, {
      stripe_subscription_id: circle.stripe_subscription_id,
      stripeSubscriptionId: circle.stripeSubscriptionId,
      subscriptionId: circle.subscriptionId,
      order_id: circle.order_id,
      orderId: circle.orderId
    });

    // Check if we have a valid subscription ID directly in the circle object
    if (circle.stripe_subscription_id &&
      typeof circle.stripe_subscription_id === 'string' &&
      circle.stripe_subscription_id.startsWith('sub_')) {
      console.log(`Pre-API check: Found valid subscription ID for ${circle.name}: ${circle.stripe_subscription_id}`);
      return circle.stripe_subscription_id;
    }

    // Check alternative property names
    if (circle.stripeSubscriptionId &&
      typeof circle.stripeSubscriptionId === 'string' &&
      circle.stripeSubscriptionId.startsWith('sub_')) {
      console.log(`Pre-API check: Found valid subscription ID (alt property) for ${circle.name}: ${circle.stripeSubscriptionId}`);
      return circle.stripeSubscriptionId;
    }

    if (circle.subscriptionId &&
      typeof circle.subscriptionId === 'string' &&
      circle.subscriptionId.startsWith('sub_')) {
      console.log(`Pre-API check: Found valid subscription ID (alt property 2) for ${circle.name}: ${circle.subscriptionId}`);
      return circle.subscriptionId;
    }

    // Check if the circle has the order_number property
    if (circle.order_number) {
      console.log(`Pre-API check: Using order number for ${circle.name}: ${circle.order_number}`);
      return circle.order_number;
    }

    // Add "Pre-API check" prefix to make it clear this is before the API call
    console.log(`Pre-API check: No valid subscription ID found for ${circle.name}, API call will be needed`);
    return null;
  };

  // Fixed renderPortActions function with proper status checks for ESIM Initiated
  // and passing subscription ID to onPortAction
  const renderPortActions = () => {
    // Get the main values we need to check - convert to lowercase for case-insensitive comparison
    const status = getSubscriptionStatus(circle).toLowerCase();
    const joiningCondition = getJoiningCondition(circle).toLowerCase();
    const shippingMethod = getShippingMethod(circle).toLowerCase();
    const isJoinedCircle = circle.source === 'subscription';
    const subscriptionId = getSubscriptionId(circle);

    // Debug output - uncomment when debugging
    console.log(`Circle ${circle.name} status check:`, {
      rawStatus: getSubscriptionStatus(circle),
      processedStatus: status,
      joiningCondition,
      shippingMethod,
      isJoinedCircle,
      subscriptionId,
      circle: circle // Log the whole circle object to see all properties
    });

    // Enhanced condition checks that handle more variations
    const isSwitchingCarriers = joiningCondition.includes('switch') ||
      joiningCondition.includes('port') ||
      joiningCondition.includes('carriers');

    const isShipmentStatus = status.includes('ship') || status.includes('sim');

    // UPDATED: Include ESIM Initiated in status checks
    const isActiveOrEsimInitiated = status.includes('active') ||
      status.includes('approved') ||
      status.includes('activate') ||
      status.includes('esim_initiated') ||
      getSubscriptionStatus(circle) === 'ESIM Initiated'; // Add exact match for display status too

    const isPortInProgress = status.includes('port') && status.includes('progress');

    const isStandardShipping = shippingMethod.includes('standard') ||
      shippingMethod.includes('physical');

    // FIXED: Better eSIM detection with lowercase and more variations
    const isEsimShipping = shippingMethod.includes('esim') ||
      shippingMethod.includes('e-sim') ||
      shippingMethod.includes('digital') ||
      shippingMethod === 'esim' ||
      shippingMethod === 'eSIM' ||  // Exact match for 'eSIM'
      shippingMethod.toUpperCase() === 'ESIM';

    // Additional enhanced logging for status matching
    console.log(`Enhanced status check for ${circle.name}:`, {
      rawStatus: getSubscriptionStatus(circle),
      exactDisplayStatus: getSubscriptionStatus(circle),
      isExactEsimInitiated: getSubscriptionStatus(circle) === 'ESIM Initiated',
      lowercaseStatus: status,
      matchesEsimInitiated: status.includes('esim_initiated'),
      finalResult: isActiveOrEsimInitiated
    });

    // Debug the condition results
    console.log(`Circle ${circle.name} conditions:`, {
      isSwitchingCarriers,
      isActiveOrEsimInitiated,
      isShipmentStatus,
      isEsimShipping,
      isStandardShipping,
      isPortInProgress,
      showPortButton: isActiveOrEsimInitiated && isEsimShipping && isSwitchingCarriers,
      shippingMethodOriginal: getShippingMethod(circle)
    });

    // If not switching carriers, don't show any buttons
    if (!isSwitchingCarriers) {
      return null;
    }

    // Show "Confirm SIM" button for physical SIM in shipment status
    // or joined circles with Shipment display status
    if ((isShipmentStatus || (isJoinedCircle && circle.statusDisplay === "Shipment")) &&
      isStandardShipping &&
      isSwitchingCarriers) {
      return (
        <motion.div
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Button
            size="small"
            variant="outlined"
            startIcon={<Check size={16} />}
            onClick={(e) => {
              e.stopPropagation(); // Prevent card selection
              if (onPortAction) {
                // Pass the circle, action, and subscription ID (if available)
                onPortAction(circle, 'confirm_sim', subscriptionId);
              }
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '12px',
              color: darkMode ? '#4CAF50' : '#10B981',
              borderColor: darkMode ? 'rgba(76, 175, 80, 0.5)' : 'rgba(16, 185, 129, 0.5)',
              '&:hover': {
                borderColor: darkMode ? 'rgba(76, 175, 80, 0.8)' : 'rgba(16, 185, 129, 0.8)',
                backgroundColor: darkMode ? 'rgba(76, 175, 80, 0.1)' : 'rgba(16, 185, 129, 0.1)'
              },
              py: 0.5,
              fontSize: '0.75rem',
              zIndex: 5,
              position: 'relative'
            }}
          >
            Confirm SIM
          </Button>
        </motion.div>
      );
    }

    // For eSIM with active/ESIM Initiated status - UPDATED to handle both status values
    if (isActiveOrEsimInitiated && isEsimShipping && isSwitchingCarriers) {
      return (
        <motion.div
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
        >
          <Button
            size="small"
            variant="outlined"
            startIcon={<Phone size={16} />}
            onClick={(e) => {
              e.stopPropagation(); // Prevent card selection
              if (onPortAction) {
                // Pass the circle, action, and subscription ID (if available)
                onPortAction(circle, 'initiate_port', subscriptionId);
              }
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '12px',
              color: darkMode ? '#2196F3' : '#0EA5E9',
              borderColor: darkMode ? 'rgba(33, 150, 243, 0.5)' : 'rgba(14, 165, 233, 0.5)',
              '&:hover': {
                borderColor: darkMode ? 'rgba(33, 150, 243, 0.8)' : 'rgba(14, 165, 233, 0.8)',
                backgroundColor: darkMode ? 'rgba(33, 150, 243, 0.1)' : 'rgba(14, 165, 233, 0.1)'
              },
              py: 0.5,
              fontSize: '0.75rem',
              zIndex: 5,
              position: 'relative'
            }}
          >
            Port Number
          </Button>
        </motion.div>
      );
    }

    // For port in progress
    if (isPortInProgress) {
      return (
        <Chip
          icon={<Clock size={14} />}
          label="Port in Progress"
          size="small"
          sx={{
            backgroundColor: darkMode ? 'rgba(255, 152, 0, 0.15)' : 'rgba(255, 152, 0, 0.1)',
            color: '#FF9800',
            fontWeight: 600,
            height: 24,
            fontSize: '0.7rem',
            borderRadius: '12px',
            border: '1px solid',
            borderColor: darkMode ? 'rgba(255, 152, 0, 0.3)' : 'rgba(255, 152, 0, 0.2)',
            '& .MuiChip-icon': { color: '#FF9800' },
            zIndex: 5,
            position: 'relative'
          }}
        />
      );
    }

    // No conditions met, don't show any port action buttons
    return null;
  };

  // Check if port actions are available
  const portActionsContent = renderPortActions();
  const hasPortActions = !!portActionsContent;

  return (
    <Paper
      onClick={() => onSelect(circle)}
      className="circle-card"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      component={motion.div}
      whileHover={{
        y: -4,
        boxShadow: darkMode ? '0 12px 24px rgba(0,0,0,0.25)' : '0 12px 24px rgba(0,0,0,0.06)'
      }}
      sx={{
        borderRadius: '24px',
        overflow: 'hidden',
        position: 'relative',
        boxShadow: darkMode ? '0 4px 20px rgba(0,0,0,0.15)' : '0 4px 16px rgba(0,0,0,0.03)',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        border: 'none',
        background: darkMode ? 'rgba(31, 41, 55, 0.5)' : 'white',
        backdropFilter: 'blur(10px)'
      }}
    >
      {/* Card Header with Status Chips */}
      <Box sx={{
        display: 'flex',
        position: 'absolute',
        top: 12,
        right: 12,
        gap: 1,
        zIndex: 2
      }}>
        <Chip
          icon={
            getSubscriptionStatus(circle).toLowerCase() === 'active' ? <Check size={14} /> :
              getSubscriptionStatus(circle).toLowerCase().includes('cancel') ? <Clock size={14} /> :
                getSubscriptionStatus(circle).toLowerCase().includes('ship') ? <Smartphone size={14} /> :
                  getSubscriptionStatus(circle).toLowerCase().includes('pending') ? <AlertTriangle size={14} /> :
                    <AlertTriangle size={14} />
          }
          label={getSubscriptionStatus(circle) || "Unknown"}
          size="small"
          sx={{
            backgroundColor: alpha(getStatusColor(getSubscriptionStatus(circle) || ""), darkMode ? 0.2 : 0.15),
            color: getStatusColor(getSubscriptionStatus(circle) || ""),
            fontWeight: 600,
            height: 28,
            fontSize: '0.75rem',
            borderRadius: '14px',
            border: `1px solid ${alpha(getStatusColor(getSubscriptionStatus(circle) || ""), darkMode ? 0.3 : 0.2)}`,
            '& .MuiChip-icon': { color: getStatusColor(getSubscriptionStatus(circle) || "") }
          }}
        />

        {circle.isOwner && (
          <Chip
            icon={<Crown size={14} />}
            label="Owner"
            size="small"
            sx={{
              backgroundColor: darkMode ? 'rgba(255, 215, 0, 0.15)' : 'rgba(255, 215, 0, 0.1)',
              color: '#FFD700',
              fontWeight: 600,
              height: 28,
              fontSize: '0.75rem',
              borderRadius: '14px',
              border: '1px solid',
              borderColor: darkMode ? 'rgba(255, 215, 0, 0.3)' : 'rgba(255, 215, 0, 0.2)',
              '& .MuiChip-icon': { color: '#FFD700' }
            }}
          />
        )}
      </Box>

      {/* Circle Card Main Content */}
      <Box sx={{
        p: 3,
        color: darkMode ? 'rgba(255,255,255,0.9)' : '#333',
        flexGrow: 1,
        display: 'flex',
        flexDirection: 'column',
      }}>
        {/* Circle Name */}
        <Box sx={{ mb: 1 }}>
          <Typography variant="h6" fontWeight={700} sx={{
            color: darkMode ? 'rgba(255,255,255,0.9) !important' : '#1A1B25 !important',
            fontSize: '1.1rem',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            pr: 8 // Space for status chips
          }}>
            {circle.name}
          </Typography>
        </Box>

        {/* Plan Name */}
        <Typography variant="body2" sx={{
          mb: 2.5,
          color: darkMode ? 'rgba(255,255,255,0.7)' : '#767991'
        }}>
          {circle.plan}
        </Typography>

        {/* Features chips */}
        <Box sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: 1,
          mb: 3
        }}>
          {/* Feature chips would go here */}
        </Box>

        {/* Member count with progress indicator */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 1
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Users size={14} style={{
                marginRight: '6px',
                color: darkMode ? 'rgba(255,255,255,0.7)' : '#767991'
              }} />
              <Typography
                variant="caption"
                fontWeight={600}
                color={darkMode ? 'rgba(255,255,255,0.7)' : '#767991'}
              >
                MEMBERS
              </Typography>
            </Box>
            <Typography
              variant="caption"
              fontWeight={700}
              color={darkMode ? 'rgba(255,255,255,0.9)' : '#1A1B25'}
            >
              {/* Total occupancy = family_size + total_joiners */}
              {(circle.family_size || 0) + (circle.total_joiners || 0)}/{circle.maxCapacity || 10}
            </Typography>
          </Box>

          {/* Progress bar with carrier color */}
          <Box sx={{
            width: '100%',
            height: 6,
            bgcolor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.05)',
            borderRadius: '6px',
            overflow: 'hidden'
          }}>
            <LinearProgress
              variant="determinate"
              value={Math.min(100, (((circle.family_size || 0) + (circle.total_joiners || 0)) / (circle.maxCapacity || 10)) * 100)}
              sx={{
                height: '100%',
                borderRadius: '6px',
                backgroundColor: 'transparent',
                '& .MuiLinearProgress-bar': {
                  backgroundColor: getCarrierColor(circle.carrier),
                  borderRadius: '6px'
                }
              }}
            />
          </Box>
        </Box>

        {/* Port Actions Section - IMPROVED POSITION */}
        {hasPortActions && (
          <Box sx={{
            mb: 3,
            display: 'flex',
            justifyContent: 'center'
          }}>
            {portActionsContent}
          </Box>
        )}

        {/* Issues Section */}
        {circle.hasIssues && circle.issues && circle.issues.length > 0 && (
          <Box sx={{
            mt: 'auto',
            mb: 3,
            p: 2,
            borderRadius: 2,
            backgroundColor: darkMode ? 'rgba(255, 152, 0, 0.15)' : 'rgba(255, 152, 0, 0.05)',
            border: '1px solid',
            borderColor: darkMode ? 'rgba(255, 152, 0, 0.3)' : 'rgba(255, 152, 0, 0.2)'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <AlertTriangle size={16} color="#FF9800" style={{ marginRight: '8px' }} />
              <Typography
                variant="subtitle2"
                fontWeight={600}
                sx={{ color: '#FF9800 !important' }}
              >
                {getSubscriptionStatus(circle).toLowerCase().includes('cancel') ? 'Subscription Ending' : 'Issues Requiring Attention'}
              </Typography>
            </Box>

            {circle.issues.map((issue, index) => (
              <Box key={index} sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                <Box sx={{
                  width: 6,
                  height: 6,
                  borderRadius: '50%',
                  bgcolor: '#FF9800',
                  mr: 1.5
                }} />
                <Typography
                  variant="body2"
                  sx={{
                    color: darkMode ? 'rgba(255,255,255,0.7) !important' : '#767991 !important'
                  }}
                >
                  {issue}
                </Typography>
              </Box>
            ))}
          </Box>
        )}

        {/* Card Footer with Share Button and Price Display */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mt: 'auto',
          pt: 2,
          borderTop: '1px solid',
          borderColor: darkMode ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)',
          position: 'relative',
          zIndex: 2
        }}>
          {/* Share Circle Button */}
          <Box onClick={(e) => onShareCircle(e)}>
            <ShareCircleButton
              circle={{
                id: circle.id,
                name: circle.name,
                carrier: circle.carrier,
                monthlyPayment: pricing.currentPrice,
                available_slots: circle.available_slots,
                features: circle.features
              }}
              darkMode={darkMode}
            />
          </Box>

          {/* Price Display with Next Tier Info + Loading State */}
          <Box sx={{ display: 'flex', alignItems: 'flex-end', position: 'relative' }}>
            {isPricingLoading ? (
              // Show loading skeleton when pricing data isn't ready
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                <Skeleton width={60} height={32} animation="wave" />
                <Skeleton width={80} height={16} animation="wave" />
              </Box>
            ) : (
              // Show pricing data when available
              <Box sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'flex-end'
              }}>
                {/* Current Price */}
                <Typography
                  variant="h6"
                  fontWeight={700}
                  sx={{
                    color: darkMode ? 'rgba(255,255,255,0.9)' : '#1A1B25',
                    mr: 0.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5
                  }}
                >
                  ${formatCurrency(pricing.currentPrice)}
                  <Typography
                    variant="caption"
                    sx={{
                      color: darkMode ? 'rgba(255,255,255,0.6)' : '#767991',
                      mb: 0.5
                    }}
                  >
                    /mo
                  </Typography>
                </Typography>

                {/* Next Tier Price - Show if different from current */}
                {pricing.nextTierPrice &&
                  pricing.currentPrice !== pricing.nextTierPrice &&
                  !pricing.isMaxTier && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        mt: -0.5
                      }}
                    >
                      <motion.div
                        animate={{ x: isHovered ? [0, 5, 0] : 0 }}
                        transition={{ repeat: isHovered ? Infinity : 0, duration: 1.5 }}
                      >
                        <ArrowRight
                          size={12}
                          style={{
                            color: darkMode ? 'rgba(74, 222, 128, 0.8)' : '#22c55e',
                            marginRight: '2px'
                          }}
                        />
                      </motion.div>
                      <Typography
                        variant="caption"
                        sx={{
                          color: darkMode ? 'rgba(74, 222, 128, 0.9)' : '#22c55e',
                          fontWeight: 600,
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        ${formatCurrency(pricing.nextTierPrice)}/mo
                      </Typography>
                    </Box>
                  )}

                {/* Max Tier Indicator */}
                {pricing.isMaxTier && (
                  <Typography
                    variant="caption"
                    sx={{
                      color: darkMode ? 'rgba(74, 222, 128, 0.9)' : '#22c55e',
                      fontWeight: 600,
                      display: 'flex',
                      alignItems: 'center',
                      mt: -0.5
                    }}
                  >
                    <Check size={10} style={{ marginRight: '2px' }} />
                    Max tier
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      {/* Button: Manage for owned circles, View/Join for others */}
      <Button
        fullWidth
        sx={{
          py: 1.5,
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          borderBottomLeftRadius: '24px',
          borderBottomRightRadius: '24px',
          background: circle.isOwner
            ? 'linear-gradient(to right, #FF4775, #FF7676)'
            : getCarrierColor(circle.carrier),
          color: 'white',
          fontWeight: 700,
          border: 'none',
          '&:hover': {
            background: circle.isOwner
              ? 'linear-gradient(to right, #FF3565, #FF6565)'
              : getCarrierColor(circle.carrier),
            filter: 'brightness(90%)'
          }
        }}
      >
        {circle.isOwner ? 'MANAGE CIRCLE' : 'VIEW DETAILS'}
      </Button>
    </Paper>
  );
};

/**
 * EmptyStateView Component
 * Displays when no circles are found
 */
const EmptyStateView = ({ activeTab, darkMode = false, onCreateCircle }) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
    >
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 8,
        px: 3,
        bgcolor: darkMode ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)',
        borderRadius: '24px',
        border: 'none',
        boxShadow: 'none'
      }}>
        <Box sx={{
          width: 80,
          height: 80,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: darkMode ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.03)',
          mb: 2,
          boxShadow: darkMode ? '0 4px 12px rgba(0,0,0,0.1)' : '0 4px 12px rgba(0,0,0,0.03)'
        }}>
          <Users size={36} color={darkMode ? 'rgba(255,255,255,0.6)' : '#767991'} />
        </Box>
        <Typography
          variant="h6"
          fontWeight={600}
          gutterBottom
          color={darkMode ? 'rgba(255,255,255,0.9)' : undefined}
        >
          {activeTab === 0 && "No subscription circles"}
          {activeTab === 1 && "You don't own any circles"}
          {activeTab === 2 && "You haven't joined any circles"}
        </Typography>
        <Typography
          variant="body2"
          color={darkMode ? 'rgba(255,255,255,0.7)' : 'text.secondary'}
          textAlign="center"
          sx={{ mb: 4, maxWidth: 'sm' }}
        >
          {activeTab === 0 && "You don't have any subscription circles at the moment."}
          {activeTab === 1 && "Create a circle to invite others and share subscription costs."}
          {activeTab === 2 && "Join existing circles to save money on your subscription plans."}
        </Typography>

        {activeTab === 1 && (
          <Button
            variant="contained"
            component={motion.button}
            whileHover={{
              scale: 1.03,
              boxShadow: darkMode ? '0 6px 16px rgba(255,71,117,0.4)' : '0 6px 16px rgba(255,71,117,0.3)'
            }}
            whileTap={{ scale: 0.98 }}
            startIcon={<Plus size={16} />}
            onClick={onCreateCircle}
            sx={{
              bgcolor: '#FF4775',
              background: 'linear-gradient(45deg, #FF4775, #FF7676)',
              borderRadius: '16px',
              boxShadow: darkMode ? '0 4px 12px rgba(255,71,117,0.3)' : '0 4px 12px rgba(255,71,117,0.2)',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1.5,
              '&:hover': {
                bgcolor: '#FF5985',
              }
            }}
          >
            Create a Circle
          </Button>
        )}

        {activeTab === 2 && (
          <Button
            variant="contained"
            component={motion.button}
            whileHover={{
              scale: 1.03,
              boxShadow: darkMode ? '0 6px 16px rgba(78,111,255,0.4)' : '0 6px 16px rgba(78,111,255,0.3)'
            }}
            whileTap={{ scale: 0.98 }}
            startIcon={<Users size={16} />}
            sx={{
              bgcolor: '#4E6FFF',
              background: 'linear-gradient(45deg, #4E6FFF, #5E7FFF)',
              borderRadius: '16px',
              boxShadow: darkMode ? '0 4px 12px rgba(78,111,255,0.3)' : '0 4px 12px rgba(78,111,255,0.2)',
              textTransform: 'none',
              fontWeight: 600,
              px: 3,
              py: 1.5,
              '&:hover': {
                bgcolor: '#3D5CFF',
              }
            }}
          >
            Browse Circles
          </Button>
        )}
      </Box>
    </motion.div>
  );
};

export default CircleTabListView;